import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { adminFeaturesKey } from "./adminFeatures";

/** "subagent" = a spawn-only blueprint (api/subagentBlueprints). */
export type AgentKind = "standard" | "family" | "subagent";
export type FamilyBlock = components["schemas"]["Family"];

export interface AgentConfigResponse {
  template: string;
  name: string | null;
  blueprint: string | null;
  telegram_bot_token: string | null;
  telegram_bot_token_masked: string | null;
  email_whitelist: string[];
  special_instructions: string;
  kind: AgentKind;
  family: FamilyBlock | null;
}

interface Scope {
  customerId: string;
  agentTemplate: string;
}

export function useAgentConfig(scope: Scope | null) {
  return useQuery({
    queryKey: ["agent-config", scope?.customerId, scope?.agentTemplate],
    enabled: scope !== null,
    queryFn: ({ signal }) =>
      apiFetch<AgentConfigResponse>(
        `/api/customers/${scope!.customerId}/agents/${scope!.agentTemplate}/config`,
        { signal },
      ),
    staleTime: 10_000,
  });
}

export interface ConfiguredAgent {
  template: string;
  name: string | null;
  blueprint: string | null;
  has_telegram: boolean;
  kind: AgentKind;
}

// All agents currently in the customer's manifest, with their
// editable display name (config.name) and connection status. This
// is the same source the org chart reads from — using it on the
// dashboard means renames in the org editor appear immediately
// without waiting for a redeploy to flow through the manifest.
export function useConfiguredAgents(customerId: string | null) {
  return useQuery({
    queryKey: ["configured-agents", customerId],
    enabled: customerId !== null,
    queryFn: ({ signal }) =>
      apiFetch<{ agents: ConfiguredAgent[] }>(
        `/api/customers/${customerId}/agents/configured`,
        { signal },
      ),
    staleTime: 2_000,
  });
}

interface PatchArgs extends Scope {
  patch: {
    name?: string;
    blueprint?: string | null;
    telegram_bot_token?: string | null;
    email_whitelist?: string[];
    special_instructions?: string;
    family?: FamilyBlock;
  };
}

export function useUpdateAgentConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, agentTemplate, patch }: PatchArgs) =>
      apiFetch(
        `/api/customers/${customerId}/agents/${agentTemplate}/config`,
        { method: "PATCH", body: patch },
      ),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: ["agent-config", vars.customerId, vars.agentTemplate],
      });
    },
  });
}

interface CreateArgs {
  customerId: string;
  template: string;
  name?: string;
  blueprint?: string;
  telegramToken?: string;
  specialInstructions?: string;
  /** "family" needs `family` (a child's helper). */
  kind?: AgentKind;
  family?: FamilyBlock;
}

export function useCreateAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, template, name, blueprint, telegramToken, specialInstructions, kind, family }: CreateArgs) =>
      apiFetch(`/api/customers/${customerId}/agents`, {
        method: "POST",
        body: {
          template,
          name,
          blueprint,
          telegram_bot_token: telegramToken,
          special_instructions: specialInstructions,
          kind: kind ?? "standard",
          family,
        },
      }),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["configured-agents"] });
      void qc.invalidateQueries({ queryKey: ["customer-agents"] });
      void qc.invalidateQueries({ queryKey: ["org-chart", vars.customerId] });
    },
  });
}

interface DeleteArgs {
  customerId: string;
  agentTemplate: string;
  /** Also hard-delete the agent's Tier 2 data (memories,
   * handoffs, activity, tasks, team membership, etc.) in the
   * same request. Defaults to true so the dashboard starts
   * from a clean slate when an agent is removed. */
  wipe?: boolean;
}

export function useDeleteAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, agentTemplate, wipe = true }: DeleteArgs) => {
      const url = `/api/customers/${customerId}/agents/${agentTemplate}${
        wipe ? "?wipe=1" : ""
      }`;
      try {
        return await apiFetch(url, { method: "DELETE" });
      } catch (err) {
        // 404 = already gone, treat as success.
        if ((err as { status?: number }).status === 404) return { ok: true };
        throw err;
      }
    },
    onSuccess: (_, vars) => {
      // Refetch everything that could be showing stale per-agent
      // data. The TeamPanel reads shared-learnings, which
      // lives in Tier 2 and need to re-render after a
      // wipe. Activity + tasks matter for any future per-agent
      // panels that survive the delete (none today, but cheap
      // to invalidate).
      void qc.invalidateQueries({ queryKey: ["configured-agents"] });
      void qc.invalidateQueries({ queryKey: ["customer-agents"] });
      void qc.invalidateQueries({ queryKey: ["org-chart", vars.customerId] });
      void qc.invalidateQueries({
        queryKey: ["shared-learnings", vars.customerId],
      });
      void qc.invalidateQueries({ queryKey: ["teams", vars.customerId] });
      void qc.invalidateQueries({
        queryKey: ["agent-activity", vars.customerId, vars.agentTemplate],
      });
      void qc.invalidateQueries({
        queryKey: ["agent-tasks", vars.customerId, vars.agentTemplate],
      });
    },
  });
}

export function useTelegramUsers(customerId: string | null) {
  return useQuery({
    queryKey: ["telegram-users", customerId],
    enabled: customerId !== null,
    queryFn: ({ signal }) =>
      apiFetch<{ users: string[] }>(
        `/api/customers/${customerId}/telegram-users`,
        { signal },
      ),
    staleTime: 10_000,
  });
}

export function useSetTelegramUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, users }: { customerId: string; users: string[] }) =>
      apiFetch(`/api/customers/${customerId}/telegram-users`, {
        method: "PUT",
        body: { users },
      }),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ["telegram-users", vars.customerId] });
    },
  });
}

export function useDeploy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) =>
      apiFetch(`/api/customers/${customerId}/deploy`, { method: "POST" }),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

// ─── inference provider (customer-wide) ───

export type InferenceProvider =
  | "vllm"
  | "openrouter"
  | "corvus_gateway"
  | "corvus_inference";

export interface InferenceProviderState {
  provider: InferenceProvider;
  has_openrouter_key: boolean;
  openrouter_model: string;
  has_corvus_gateway_key: boolean;
  corvus_gateway_model: string;
  has_corvus_inference_key: boolean;
  corvus_inference_model: string;
}

export function useInferenceProvider(customerId: string | null) {
  return useQuery({
    queryKey: ["inference-provider", customerId],
    enabled: !!customerId,
    queryFn: () =>
      apiFetch<InferenceProviderState>(
        `/api/customers/${customerId}/inference-provider`,
      ),
    staleTime: 30_000,
  });
}

export function useSetInferenceProvider() {
  const qc = useQueryClient();
  return useMutation({
    // Settings › Models and Admin › Model show a refusal next to its field.
    meta: { errorHandled: true },
    mutationFn: ({
      customerId,
      provider,
      api_key,
      openrouter_model,
      corvus_gateway_api_key,
      corvus_gateway_model,
      corvus_inference_api_key,
      corvus_inference_model,
    }: {
      customerId: string;
      provider: InferenceProvider;
      /** Paste a new key, pass null/"" to clear, or omit to leave unchanged. */
      api_key?: string | null;
      /** OpenRouter model id; omit to leave unchanged. */
      openrouter_model?: string;
      /** Corvus Gateway key; paste to set, null/"" to clear, omit to keep. */
      corvus_gateway_api_key?: string | null;
      /** Corvus Gateway model id; omit to leave unchanged. */
      corvus_gateway_model?: string;
      /** Corvus Inference key; paste to set, null/"" to clear, omit to keep. */
      corvus_inference_api_key?: string | null;
      /** Corvus Inference model id; omit to leave unchanged. */
      corvus_inference_model?: string;
    }) => {
      const body: Record<string, unknown> = { provider };
      if (api_key !== undefined) body.api_key = api_key;
      if (openrouter_model !== undefined) body.openrouter_model = openrouter_model;
      if (corvus_gateway_api_key !== undefined)
        body.corvus_gateway_api_key = corvus_gateway_api_key;
      if (corvus_gateway_model !== undefined)
        body.corvus_gateway_model = corvus_gateway_model;
      if (corvus_inference_api_key !== undefined)
        body.corvus_inference_api_key = corvus_inference_api_key;
      if (corvus_inference_model !== undefined)
        body.corvus_inference_model = corvus_inference_model;
      return apiFetch<InferenceProviderState>(
        `/api/customers/${customerId}/inference-provider`,
        { method: "PUT", body },
      );
    },
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({
        queryKey: ["inference-provider", vars.customerId],
      });
      void qc.invalidateQueries({ queryKey: ["model-policy", vars.customerId] });
      // the stored key's state rides on the Corvus Inference list
      void qc.invalidateQueries({ queryKey: ["corvus-inference-models", vars.customerId] });
      // an admin's change shows in Admin › Features' Model column (inactive elsewhere: nothing fetched)
      void qc.invalidateQueries({ queryKey: adminFeaturesKey });
    },
    onError: (e, vars) => {
      // 502 "saved but failed to apply live": it IS saved, so show what is stored now
      if (e instanceof ApiError && e.status === 502) {
        void qc.invalidateQueries({ queryKey: ["inference-provider", vars.customerId] });
        void qc.invalidateQueries({ queryKey: ["model-policy", vars.customerId] });
        void qc.invalidateQueries({ queryKey: adminFeaturesKey });
      }
      // 403 model_locked: the administrator locked it while the form was open;
      // the page turns into the locked line instead of a form that cannot save
      if (e instanceof ApiError && e.status === 403) {
        void qc.invalidateQueries({ queryKey: ["model-policy", vars.customerId] });
      }
    },
  });
}

export interface OpenRouterModel {
  id: string;
  name: string;
  context_length: number | null;
  prompt_price: string | null;
}

// Compatible OpenRouter models (text + tool-calling), for the model
// dropdown. Auto-loaded when the OpenRouter provider is selected. Cached
// 1h server-side; 5min client-side.
export function useOpenRouterModels(customerId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["openrouter-models", customerId],
    enabled: !!customerId && enabled,
    queryFn: ({ signal }) =>
      apiFetch<OpenRouterModel[]>(
        `/api/customers/${customerId}/openrouter/models`,
        { signal },
      ),
    staleTime: 300_000,
    retry: false,
  });
}

export interface CorvusGatewayModel {
  id: string;
  name: string;
}

// Models served by the Corvus Gateway (ids only — no ctx/price). Auto-loaded
// when the Corvus Gateway provider is selected. Cached 1h server-side.
export function useCorvusGatewayModels(
  customerId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["corvus-gateway-models", customerId],
    enabled: !!customerId && enabled,
    queryFn: ({ signal }) =>
      apiFetch<CorvusGatewayModel[]>(
        `/api/customers/${customerId}/corvus-gateway/models`,
        { signal },
      ),
    staleTime: 300_000,
    retry: false,
  });
}

export interface CorvusInferenceModel {
  id: string;
  name: string;
}

// The Corvus Inference models a workspace may choose (Kimi K3, Qwen 27B, by
// name). The server checks the workspace's key with the hub (cached per key):
// a 409 key_rejected means the stored key is turned down.
export function useCorvusInferenceModels(
  customerId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["corvus-inference-models", customerId],
    enabled: !!customerId && enabled,
    queryFn: ({ signal }) =>
      apiFetch<CorvusInferenceModel[]>(
        `/api/customers/${customerId}/corvus-inference/models`,
        { signal },
      ),
    staleTime: 300_000,
    retry: false,
  });
}

export function useResetMattermost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (customerId: string) =>
      apiFetch<{ ok: boolean; stdout: string; stderr: string }>(
        `/api/customers/${customerId}/mattermost/reset`,
        { method: "POST" },
      ),
    onSuccess: () => {
      // Reset destroys every Mattermost identity, every bot token,
      // every channel id. Anything cached client-side that referenced
      // those is now wrong. Blow away the whole query cache to force
      // fresh state on the next render — including the iframe's
      // _session call, which will mint new MMAUTHTOKEN cookies against
      // the new admin user.
      void qc.invalidateQueries();
    },
  });
}

// Hard-restart a single agent's OpenClaw gateway. Use when the agent is
// stuck mid-tool-call and /reset in chat isn't enough. Preserves on-disk
// state (skills, memories, sessions, work queue) — only the live event
// loop is lost.
export function useRestartAgent() {
  return useMutation({
    mutationFn: ({
      customerId,
      template,
    }: {
      customerId: string;
      template: string;
    }) =>
      apiFetch<{ ok: boolean; stdout: string; stderr: string }>(
        `/api/customers/${customerId}/agents/${template}/restart`,
        { method: "POST" },
      ),
  });
}
